// ============================================================================
// ehrItSetupGuide.js — SMART Build 2P (pure; EHR Integration Onboarding Pack)
//
// Turns a tenant EHR connection (the SAME never-PHI, never-secret config doc the
// Connection Settings form + Sandbox Console already read) into the copyable
// values and static onboarding content hospital/customer IT needs to connect
// ReferralGate's generic SMART-on-FHIR entrance.
//
// This is the IT SETUP GUIDE model — an admin/onboarding surface, NOT a new EHR
// feature. It changes no launch/callback/token/import/Gate behavior. It reuses
// the canonical launch/redirect/scope builders from smartSandboxConsole.js so the
// registered values can never drift between the two surfaces.
//
// It NEVER emits secrets, tokens, client secrets, raw fhirUser, fhirUserHash, PHI,
// launch state, codeVerifier, or authorization codes. Client ID is a PUBLIC value
// for PKCE clients and is safe to display/copy.
// ============================================================================

import {
  SMART_APP_HOST,
  SMART_REQUIRED_SCOPES,
  SMART_APP_LAUNCHER_URL,
  buildSmartLaunchUrl,
  buildSmartRedirectUri,
} from './smartSandboxConsole.js';

// Re-export the launcher URL so the panel imports one module.
export { SMART_APP_LAUNCHER_URL };

// The registered SMART app display name.
export const SMART_APP_NAME = 'ReferralGate';

// Roles allowed to view the IT Setup Guide. Mirrors the Sandbox Console gate:
// platform_admin (any tenant) + tenant_admin (own tenant). No GP / non-admin.
const IT_SETUP_GUIDE_ROLES = new Set(['platform_admin', 'tenant_admin']);

/** Visibility gate — platform_admin + tenant_admin only (no GP / non-admin). */
export function canViewItSetupGuide(role) {
  return IT_SETUP_GUIDE_ROLES.has(role);
}

// ── Dynamic, connection-specific copyable values ──────────────────────────────

/**
 * Assemble the safe IT Setup Guide model from a never-PHI connection doc. Only
 * allowlisted config values are surfaced (no secrets / tokens / PHI / raw
 * fhirUser). Launch URL + Redirect URI come from the canonical builders so they
 * match the Sandbox Console exactly. Returns nulls for values not yet configured.
 *
 * @param {{ tenantId?: string, connection?: object, host?: string }} params
 */
export function buildItSetupGuideModel({ tenantId, connection, host = SMART_APP_HOST } = {}) {
  const c = connection || {};
  const connectionId = c.connectionId || null;
  const requestedScopes = Array.isArray(c.requestedScopes) && c.requestedScopes.length
    ? c.requestedScopes.join(' ')
    : SMART_REQUIRED_SCOPES;
  const sandboxMode = c.sandboxMode !== false; // default true (safer)
  return {
    tenantId: tenantId || null,
    connectionId,
    appName: SMART_APP_NAME,
    // Canonical registration values (identical to the Sandbox Console):
    launchUrl: buildSmartLaunchUrl({ tenantId, connectionId, host }),
    redirectUri: buildSmartRedirectUri(host),
    requestedScopes,
    fhirBaseUrl: c.fhirBaseUrl || null,
    // SMART discovery doc — where authorization/token endpoints are DISCOVERED at
    // launch time. The endpoints themselves are not stored on the connection.
    smartConfigurationUrl: c.smartConfigurationUrl || null,
    clientId: c.clientId || null,                 // public PKCE client id — safe to display
    authType: c.authType || null,
    tokenEndpointAuthMethod: c.tokenEndpointAuthMethod || null,
    sandboxMode,
    modeLabel: sandboxMode ? 'Sandbox' : 'Production',
    requireUserMapping: c.requireUserMapping === true,
    sourceSystemLabel: c.sourceSystemLabel || null,
    status: c.status || null,
    hasConnection: !!connectionId,
  };
}

// ── Static onboarding content (labels only; no live PHI, no secret values) ────

// 1) Purpose
export const GUIDE_PURPOSE =
  'ReferralGate supports a generic SMART-on-FHIR entrance that lets a referring '
  + 'provider launch ReferralGate from inside the EHR patient chart. This guide '
  + 'helps hospital/customer IT register and configure that connection safely.';

// 2) What ReferralGate supports in v1
export const V1_SUPPORTED = Object.freeze([
  'SMART launch from the EHR',
  'OAuth / PKCE authorization',
  'Patient context capture',
  'Patient resource import',
  'Coverage resource import',
  'Referral Packet creation',
  'Optional supporting document upload',
  'OCR on uploaded documents',
  'Gate handoff',
  'GP confirmation before referral submit',
]);

// 3) What ReferralGate does NOT do yet (future / customer-specific)
export const V1_NOT_SUPPORTED = Object.freeze([
  'EHR writeback',
  'Bidirectional referral status sync',
  'Scheduling writeback',
  'Note writeback',
  'FHIR DocumentReference import',
  'Labs / Observation import',
  'Epic-specific custom flows',
  'Cerner / Oracle-specific custom flows',
  'Production user mapping enforcement unless configured with customer IT',
  'Refresh-token / background sync flows unless approved',
]);

// 4) Required SMART app registration scopes (values are dynamic; these are the
//    canonical v1 read scopes + what each is for).
export const APP_REGISTRATION_SCOPES = Object.freeze([
  { scope: 'launch', purpose: 'EHR launch context' },
  { scope: 'openid', purpose: 'Authentication' },
  { scope: 'fhirUser', purpose: 'Identify the launching provider (optional mapping)' },
  { scope: 'patient/Patient.r', purpose: 'Read the launched patient' },
  { scope: 'patient/Coverage.rs', purpose: 'Read/search the patient coverage' },
]);

export const SCOPES_POLICY_NOTE =
  'Final scopes depend on the hospital’s EHR policy. ReferralGate v1 requests '
  + 'read-only scopes only.';

// 5) FHIR resources read in v1
export const FHIR_RESOURCES_READ = Object.freeze([
  {
    resource: 'Patient',
    fields: ['name', 'DOB', 'sex', 'phone / email if available', 'address', 'identifier / MRN if available'],
  },
  {
    resource: 'Coverage',
    fields: ['payer', 'member / subscriber identifiers if available', 'insurance metadata if available'],
  },
]);

// 6) Data handling / PHI boundary
export const DATA_HANDLING = Object.freeze([
  'PHI is stored only under PHI-protected paths.',
  'The packet root stores only safe rollups / status — never PHI.',
  'Raw access tokens are never stored on normal root documents.',
  'EHR-imported values are candidate / draft values until the GP submits.',
  'Gate submit is the final referral truth write.',
  'No PHI in logs, ops, metrics, or non-PHI audit fields.',
  'Tenant isolation is enforced on every read and write.',
]);

// 7) User identity / fhirUser mapping
export const USER_IDENTITY_MAPPING = Object.freeze([
  'Some EHRs return fhirUser on launch; some sandbox flows may not.',
  'ReferralGate supports hash-based user mapping (raw fhirUser is never stored or logged).',
  'Production enforcement (Require EHR user mapping) should only be enabled after hospital IT confirms fhirUser behavior.',
  'At least one ACTIVE mapping must exist before enforcement is turned on, or mapped GPs are blocked.',
]);

// 8) Hospital IT responsibilities (checklist)
export const HOSPITAL_IT_RESPONSIBILITIES = Object.freeze([
  'Provide the production FHIR base URL',
  'Register the ReferralGate SMART app',
  'Approve the redirect URI',
  'Provide client ID / client secret if applicable',
  'Confirm supported SMART scopes',
  'Confirm Patient and Coverage availability',
  'Confirm fhirUser behavior',
  'Confirm launch context behavior',
  'Confirm test patients',
  'Confirm security / BAA requirements',
  'Confirm the go-live approval process',
]);

// 9) ReferralGate admin responsibilities (checklist)
export const REFERRALGATE_ADMIN_RESPONSIBILITIES = Object.freeze([
  'Create the tenant',
  'Configure the EHR connection',
  'Enter the FHIR base URL',
  'Enter auth/token endpoints or discover metadata',
  'Enter the client ID',
  'Configure scopes',
  'Set sandbox / production mode',
  'Configure requireUserMapping if approved',
  'Test capability discovery',
  'Test SMART launch',
  'Test Patient / Coverage import',
  'Test Gate handoff',
]);

// 10) Sandbox test checklist (ordered)
export const SANDBOX_TEST_CHECKLIST = Object.freeze([
  'Configure the sandbox connection',
  'Open the SMART launcher',
  'Launch ReferralGate',
  'Authorize the provider',
  'Select a patient',
  'Continue in ReferralGate',
  'Create / import the packet',
  'Verify the Packet Inbox row',
  'Optionally upload a supporting document',
  'Continue to Gate',
  'Verify patient / admin / insurance fields',
  'Submit only in the test environment',
]);

// 11) Production go-live checklist
export const PRODUCTION_GOLIVE_CHECKLIST = Object.freeze([
  'Production app registration complete',
  'Redirect URI approved',
  'Scopes approved',
  'Test patient launch confirmed',
  'Patient / Coverage import confirmed',
  'User role mapping confirmed',
  'Tenant isolation confirmed',
  'PHI storage review complete',
  'Support contacts identified',
  'Rollback plan defined',
]);

// 12) Troubleshooting (symptom → likely cause / fix)
export const TROUBLESHOOTING = Object.freeze([
  { symptom: 'Issuer (iss) mismatch', fix: 'The launching EHR issuer does not match the configured FHIR base URL.' },
  { symptom: 'Invalid client ID', fix: 'The registered client ID does not match the configured connection.' },
  { symptom: 'Redirect URI mismatch', fix: 'The EHR-approved redirect URI must equal the ReferralGate callback exactly.' },
  { symptom: 'Missing patient context', fix: 'The launch did not include a patient; relaunch from a patient chart.' },
  { symptom: 'fhirUser absent', fix: 'The EHR/sandbox did not return fhirUser; disable enforcement or map after confirming behavior.' },
  { symptom: 'Insufficient scopes', fix: 'Requested scopes were not granted; confirm the approved scope set with hospital IT.' },
  { symptom: 'Patient read denied', fix: 'patient/Patient.r was not granted or the token lacks patient context.' },
  { symptom: 'Coverage read denied', fix: 'patient/Coverage.rs was not granted; Coverage may be optional for this EHR.' },
  { symptom: 'Import material missing', fix: 'The callback did not stage encrypted import material; re-run the launch.' },
  { symptom: 'User mapping required but unmapped', fix: 'Add an ACTIVE EHR user mapping before enabling enforcement.' },
  { symptom: 'Packet created but import incomplete', fix: 'Retry the import step; Patient may have imported while Coverage was unavailable.' },
]);

// 13) Future optional extensions
export const FUTURE_EXTENSIONS = Object.freeze([
  'DocumentReference import',
  'Observation / lab import',
  'Referral status writeback',
  'Scheduling writeback',
  'Note writeback',
  'Epic-specific adapter',
  'Cerner / Oracle-specific adapter',
  'Background refresh / sync',
  'Bidirectional status sync',
]);

// Boundary statement shown prominently — sets honest expectations.
export const INTEGRATION_BOUNDARY_NOTE =
  'ReferralGate provides a generic SMART-on-FHIR entrance, not a full Epic/Cerner '
  + 'integration and not an EHR certification. Advanced writeback and EHR-specific '
  + 'adapters require hospital IT collaboration.';

export default {
  SMART_APP_NAME,
  SMART_APP_LAUNCHER_URL,
  canViewItSetupGuide,
  buildItSetupGuideModel,
  GUIDE_PURPOSE,
  V1_SUPPORTED,
  V1_NOT_SUPPORTED,
  APP_REGISTRATION_SCOPES,
  SCOPES_POLICY_NOTE,
  FHIR_RESOURCES_READ,
  DATA_HANDLING,
  USER_IDENTITY_MAPPING,
  HOSPITAL_IT_RESPONSIBILITIES,
  REFERRALGATE_ADMIN_RESPONSIBILITIES,
  SANDBOX_TEST_CHECKLIST,
  PRODUCTION_GOLIVE_CHECKLIST,
  TROUBLESHOOTING,
  FUTURE_EXTENSIONS,
  INTEGRATION_BOUNDARY_NOTE,
};
